import {
  createElementObject,
  createTileLayerComponent,
  type LayerProps,
  updateGridLayer,
} from '@react-leaflet/core';
import { TileLayer, type TileLayerOptions } from 'leaflet';
import { Coloring, ColoringType, FlatLegendType } from '../../api/api.ts';
import {
  ColorBandColorizer,
  ColorScaler,
  getBandColor,
} from '../../common/coloring.ts';
import ColorizedTileLayer, { ColorizeFunction } from './ColorizedTileLayer.ts';
import chroma from 'chroma-js';

const {
  // VITE_MAPBOX_USERNAME: MAPBOX_USERNAME,
  // VITE_MAPBOX_STYLE_ID: MAPBOX_STYLE_ID,
  // VITE_MAPBOX_ACCESS_TOKEN: MAPBOX_ACCESS_TOKEN,
  VITE_BACKEND_BASE_URL: BACKEND_BASE_URL,
} = import.meta.env;

export interface TileLayerProps extends TileLayerOptions, LayerProps {
  url: string;
  coloring: Coloring;
}

const ColoredTileLayer = createTileLayerComponent<TileLayer, TileLayerProps>(
  function createTileLayer({ url, coloring, ...options }, context) {
    let colorizer: ColorizeFunction;
    if (coloring.type === ColoringType.Scale) {
      const scaler = new ColorScaler(
        coloring.colors,
        coloring.min,
        coloring.max
      );
      colorizer = (r: number, _g: number, _b: number, a: number) => {
        const color = scaler.colorize(r);
        return [color[0], color[1], color[2], a];
      };
    } else if (coloring.type === ColoringType.Flat) {
      const color = chroma(coloring.color);
      const constR = color.get('rgb.r');
      const constG = color.get('rgb.g');
      const constB = color.get('rgb.b');

      const overrideAlpha =
        coloring.type === ColoringType.Flat &&
        coloring.legend.type === FlatLegendType.TransparencyScale;

      colorizer = (r: number, _g: number, _b: number, a: number) => {
        return [constR, constG, constB, a === 0 ? 0 : overrideAlpha ? r : a];
      };
    } else if (coloring.type === ColoringType.Bands) {
      const bandColorizer = new ColorBandColorizer(coloring.bands);
      colorizer = (r: number, _g: number, _b: number, a: number) => {
        const color = bandColorizer.colorize(r);
        return [color[0], color[1], color[2], a];
      };
    } else {
      // todo other coloring types
      colorizer = (r: number, g: number, b: number, a: number) => [r, g, b, a];
    }

    // @ts-ignore @todo ts ignore resolve
    const colorizedTileLayer = new ColorizedTileLayer(BACKEND_BASE_URL + url, {
      ...options,
      colorizer: colorizer,
    });
    return createElementObject(colorizedTileLayer, context);
  },
  function updateTileLayer(layer, props, prevProps) {
    updateGridLayer(layer, props, prevProps);

    const { url } = props;
    if (url != null && url !== prevProps.url) {
      layer.setUrl(BACKEND_BASE_URL + url);
    }
  }
);

export default ColoredTileLayer;
